import { useContext } from 'react'

import {
  Columns, ModalContext, LoadingModule, SidebarModal,
} from '@domain-group/suit-theme'

import EntityModule from '@components/EntityModule'
import NotificationBannerLocation from '@components/NotificationBannerLocation'

import type { AdCopyModel } from '@models/types'

import useAdCopyReviewer from './hooks/useAdCopyReviewer'

import OrderItemDetailsModule from './components/OrderItemDetailsModule'
import AdCopyReviewItem from './components/AdCopyReviewItem'

const MODAL_KEY = 'AdCopyReviewerModal'

export type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const AdCopyReviewerModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const { campaign: initCampaign, orderItem: initOrderItem, provider: initProvider } = modalPayload

  const adCopyReviewerPayload = useAdCopyReviewer({
    campaign: initCampaign,
    orderItem: initOrderItem,
    provider: initProvider,
  })

  const {
    campaign: {
      default_image,
      id: campaignId,
      title: campaignTitle,
    },
    displayAdCopyTypes,
    loadingAdCopies,
    loadingCampaign,
    loadingOrderItem,
    loadingProvider,
    orderItem,
    orderItemAdCopiesWithContent,
    provider: {
      title: providerTitle,
    },
    providerLogoUrl,
  } = adCopyReviewerPayload

  return (
    <SidebarModal
      callbacks={callbacks}
      disableAnimation
      modalKey={MODAL_KEY}
      showModal={showModal}
      size="fullscreen"
    >
      <SidebarModal.Header callbacks={callbacks} title="Copy" titleSecondLine="Proofing" />

      <SidebarModal.Content boxProps={{ backgroundColor: 'backgroundColor' }} hasFooter={false}>
        <Columns flexDirection={['column', 'column', 'row']} boxProps={{ marginX: 'auto', maxWidth: 1400 }}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.adCopyReviewer.campaign.banner" />

            <LoadingModule loading={loadingAdCopies} times={3} />

            {!loadingAdCopies && displayAdCopyTypes?.map((adCopyType) => {
              const { key, title: adCopyTitle } = adCopyType
              const adCopy = orderItemAdCopiesWithContent?.find((a: AdCopyModel) => a.copy_type === key)

              if (!adCopy){
                return null
              }

              return (
                <AdCopyReviewItem adCopy={adCopy} campaignId={campaignId} key={key} title={adCopyTitle} />
              )
            })}
          </Columns.Main>

          <Columns.Sidebar>
            <OrderItemDetailsModule orderItem={orderItem} loading={loadingOrderItem} />

            <EntityModule entityName="Campaign" image={default_image} loading={loadingCampaign} title={campaignTitle} />

            <EntityModule
              entityName="Provider"
              image={providerLogoUrl}
              loading={loadingProvider}
              title={providerTitle}
            />
          </Columns.Sidebar>
        </Columns>
      </SidebarModal.Content>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <AdCopyReviewerModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
